import React, { useEffect, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

// WW-142.1 · Passly and looped on Sources: last run, found, new, duplicates, errors, on/off (default off).
// WW-201 · each switch has an accessible name; turning one on asks first, with a dry-run estimate.
export const FinderFeedsPanel = () => {
  const [feeds, setFeeds] = useState([]);
  const [ask, setAsk] = useState(null); // {name, n}
  const [checking, setChecking] = useState("");
  const [askRun, setAskRun] = useState(""); // WW-142.4 · feed name waiting for the run now confirm
  const load = async () => {
    try { setFeeds((await client.get("/admin/sources/feeds")).data.feeds || []); } catch { setFeeds([]); }
  };
  useEffect(() => { load(); }, []);
  const anyRunning = feeds.some((f) => f.running);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [anyRunning]);
  const runNow = async (name) => {
    try {
      const r = await client.post(`/admin/sources/feeds/${name}/run-now`);
      toast.success(r.data.started ? `${name} run started` : `${name} is already running`);
      await load();
    } catch { toast.error(`could not start ${name}`); }
  };
  const flip = async (name) => {
    try { const r = await client.post(`/admin/sources/feeds/${name}/toggle`); toast.success(`${name} ${r.data.enabled ? "on" : "off"}`); await load(); }
    catch { toast.error("could not switch"); }
  };
  const onSwitch = async (f) => {
    if (f.enabled) { await flip(f.name); return; }
    // WW-201.1 · open the confirm at once; the dry run fills in the number.
    setAsk({ name: f.name, n: undefined });
    let n = null;
    try { n = (await client.post(["passly", "looped"].includes(f.name) ? `/admin/sources/${f.name}/dry-run` : `/admin/sources/site/${f.name}/dry-run`)).data?.counts?.new ?? null; } catch { n = null; }
    setAsk((a) => (a && a.name === f.name ? { name: f.name, n } : a));
  };
  const eventsWord = (n) => (n === 1 ? "1 event" : `${n} events`);
  return (
    <div data-testid="finder-feeds-panel" className="mt-8 rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
      <p className="label-tiny">ticket site feeds · daily, pending only</p>
      <div data-testid="finder-feeds-scroll" className="mt-3 overflow-x-auto" style={{ maxWidth: "100%" }}>
      <table className="w-full text-xs whitespace-nowrap">
        <thead><tr className="text-left"><th className="p-2">feed</th><th className="p-2">last run</th><th className="p-2">found</th><th className="p-2">new</th><th className="p-2">duplicates</th><th className="p-2">errors</th><th className="p-2">on</th><th className="p-2">run</th></tr></thead>
        <tbody>
          {feeds.map((f) => {
            const r = f.last_run || {};
            return (
              <tr key={f.name} data-testid={`finder-feed-${f.name}`} className="border-t">
                <td className="p-2 font-semibold">{f.name}</td>
                <td className="p-2" data-testid={`finder-feed-${f.name}-last`}>{r.at ? `${r.at.slice(0, 16).replace("T", " ")}${r.skipped === "off" ? " · skipped, off" : ""}` : "never"}</td>
                <td className="p-2">{r.found ?? 0}</td>
                <td className="p-2">{r.created ?? 0}</td>
                <td className="p-2">{r.duplicates ?? 0}</td>
                <td className="p-2" style={{ color: r.error ? "#9A2222" : undefined }}>{r.error || "none"}</td>
                <td className="p-2">
                  <button type="button" role="switch" aria-checked={f.enabled} aria-label={`${f.name} feed on/off`}
                          data-testid={`finder-feed-${f.name}-toggle`} onClick={() => onSwitch(f)} disabled={checking === f.name}
                          className="rounded-full border px-3 py-1 font-semibold disabled:opacity-60" style={{ background: f.enabled ? "#1A1A30" : "transparent", color: f.enabled ? "#FFFFFF" : "#1A1A30", borderColor: "#1A1A30" }}>
                    {checking === f.name ? "checking…" : (f.enabled ? "on" : "off")}
                  </button>
                </td>
                <td className="p-2">
                  <button type="button" data-testid={`finder-feed-${f.name}-run-now`} aria-label={`run ${f.name} now`}
                          onClick={() => setAskRun(f.name)} disabled={!f.enabled || f.running}
                          title={f.enabled ? "" : "turn the feed on first"}
                          className="rounded-full border px-3 py-1 font-semibold disabled:opacity-50" style={{ borderColor: "#1A1A30", color: "#1A1A30" }}>
                    {f.running ? "running…" : "run now"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <ConfirmDialog
        open={!!ask}
        testIdPrefix="finder-feed-confirm"
        title={ask ? `turn ${ask.name} on?` : ""}
        message={ask ? (ask.n === undefined
          ? `counting… checking ${ask.name} for events that are not on watu wako yet.`
          : ask.n === null
            ? `couldn't count ${ask.name} just now. the first run adds its events to the review queue; after that, only new ones each day.`
            : `the first run adds about ${eventsWord(ask.n)} to the review queue; after that, only new ones each day.`) : ""}
        confirmLabel="yes, turn it on"
        confirmDisabled={!!ask && ask.n === undefined}
        onCancel={() => setAsk(null)}
        onConfirm={async () => { const n = ask.name; setAsk(null); await flip(n); }}
      />
      <ConfirmDialog
        open={!!askRun}
        testIdPrefix="finder-feed-run-confirm"
        title={askRun ? `run ${askRun} now?` : ""}
        message={askRun ? `this runs the daily ${askRun} check now. new events go to the review queue as pending, with the same duplicate and kenya checks. nothing is published.` : ""}
        confirmLabel="yes, run it now"
        onCancel={() => setAskRun("")}
        onConfirm={async () => { const n = askRun; setAskRun(""); await runNow(n); }}
      />
    </div>
  );
};
